import React from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import { IconX } from '@tabler/icons-react';
import EditableValue from './EditableValue';

const parseValueWithUnit = (val, defaultUnit = 'px') => {
    if (val === undefined || val === null || val === '') return { value: undefined, unit: defaultUnit };
    const num = parseFloat(val);
    if (isNaN(num)) return { value: undefined, unit: defaultUnit };
    const unit = String(val).replace(String(num), '').trim() || defaultUnit;
    return { value: num, unit };
};

const getSliderParams = (unit, type) => {
    switch (unit) {
        case 'px':
            if (type === 'offset') {
                return { defaultValue: -30, min: -100, max: 200, step: 1 };
            } else if (type === 'borderRadius') {
                return { defaultValue: 12, min: 0, max: 50, step: 1 };
            } else {
                // gap
                return { defaultValue: 0, min: 0, max: 150, step: 1 };
            }
        case 'em':
        case 'rem':
        case 'cqh':
        case 'cqw':
            if (type === 'offset') {
                return { defaultValue: -2, min: -10, max: 15, step: 0.1 };
            } else if (type === 'borderRadius') {
                return { defaultValue: 0.75, min: 0, max: 5, step: 0.05 };
            } else {
                // gap
                return { defaultValue: 0, min: 0, max: 10, step: 0.1 };
            }
        case '%':
        default:
            if (type === 'offset') {
                return { defaultValue: -33.33, min: -100, max: 150, step: 1 };
            } else if (type === 'borderRadius') {
                return { defaultValue: 10, min: 0, max: 50, step: 1 };
            } else {
                // gap
                return { defaultValue: 0, min: 0, max: 100, step: 1 };
            }
    }
};

const CampaignMetricsTab = ({ metrics = {}, updateLocalMetricsConfig }) => {
    const { detached = {}, offset = {}, gap = {}, borderRadius: detachedMetricsBorderRadiusRaw } = metrics;

    const detachedMetrics = detached.desktop || false;
    const detachedMetricsMobile = detached.mobile || false;
    const metricsOffsetValue = offset.value;
    const metricsOffsetUnit = offset.unit || '%';
    const metricsGapValueDesktopRaw = gap.desktop;
    const metricsGapValueMobileRaw = gap.mobile;

    const parsedBorderRadius = parseValueWithUnit(detachedMetricsBorderRadiusRaw, 'px');
    const parsedGapDesktop = parseValueWithUnit(metricsGapValueDesktopRaw, 'px');
    const parsedGapMobile = parseValueWithUnit(metricsGapValueMobileRaw, 'px');

    const offsetParams = getSliderParams(metricsOffsetUnit, 'offset');
    const borderRadiusParams = getSliderParams(parsedBorderRadius.unit, 'borderRadius');
    const gapDesktopParams = getSliderParams(parsedGapDesktop.unit, 'gap');
    const gapMobileParams = getSliderParams(parsedGapMobile.unit, 'gap');

    return (
        <div>
            <div className="mb-4">
                <h2 className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3">Kampagnen-Metriken</h2>
                <FormGroup switch className="mb-3">
                    <Input
                        type="switch"
                        id="detachedMetrics"
                        checked={detachedMetrics}
                        onChange={(e) =>
                            updateLocalMetricsConfig({
                                detached: {
                                    ...detached,
                                    desktop: e.target.checked,
                                },
                            })
                        }
                    />
                    <Label check for="detachedMetrics">
                        Metriken ablösen (Overlay)
                    </Label>
                </FormGroup>

                {detachedMetrics && (
                    <div className="ps-3 border-start mb-3">
                        <FormGroup switch className="mb-3">
                            <Input
                                type="switch"
                                id="detachedMetricsMobile"
                                checked={detachedMetricsMobile}
                                onChange={(e) =>
                                    updateLocalMetricsConfig({
                                        detached: {
                                            ...detached,
                                            mobile: e.target.checked,
                                        },
                                    })
                                }
                            />
                            <Label check for="detachedMetricsMobile">
                                Auch auf Mobilgeräten ablösen
                            </Label>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <Label className="fw-semibold" for="metricsOffsetValue">
                                    Abstand / Offset
                                </Label>
                                <EditableValue
                                    value={metricsOffsetValue}
                                    defaultValue={offsetParams.defaultValue}
                                    onChange={(val) =>
                                        updateLocalMetricsConfig({
                                            offset: {
                                                ...offset,
                                                value: val,
                                            },
                                        })
                                    }
                                    min={offsetParams.min}
                                    max={offsetParams.max}
                                    unit={metricsOffsetUnit}
                                    units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                    onUnitChange={(newUnit) => {
                                        const currentVal =
                                            metricsOffsetValue != null
                                                ? metricsOffsetValue
                                                : getSliderParams(newUnit, 'offset').defaultValue;
                                        updateLocalMetricsConfig({
                                            offset: {
                                                value: currentVal,
                                                unit: newUnit,
                                            },
                                        });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="range"
                                    id="metricsOffsetValue"
                                    className="flex-grow-1"
                                    min={offsetParams.min}
                                    max={offsetParams.max}
                                    step={offsetParams.step}
                                    value={metricsOffsetValue != null ? metricsOffsetValue : offsetParams.defaultValue}
                                    onChange={(e) =>
                                        updateLocalMetricsConfig({
                                            offset: {
                                                ...offset,
                                                value: parseFloat(e.target.value),
                                            },
                                        })
                                    }
                                    onPointerDown={() => {
                                        if (metricsOffsetValue == null) {
                                            updateLocalMetricsConfig({
                                                offset: {
                                                    ...offset,
                                                    value: offsetParams.defaultValue,
                                                },
                                            });
                                        }
                                    }}
                                    style={{
                                        opacity: metricsOffsetValue != null ? 1 : 0.5,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                        metricsOffsetValue != null
                                            ? 'btn-outline-secondary'
                                            : 'btn-outline-secondary disabled'
                                    }`}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        padding: 0,
                                        opacity: metricsOffsetValue != null ? 1 : 0.4,
                                        pointerEvents: metricsOffsetValue != null ? 'auto' : 'none',
                                    }}
                                    onClick={() =>
                                        updateLocalMetricsConfig({
                                            offset: {
                                                ...offset,
                                                value: undefined,
                                            },
                                        })
                                    }
                                    title={metricsOffsetValue != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <Label className="fw-semibold" for="detachedMetricsBorderRadius">
                                    Eckenradius (Overlay)
                                </Label>
                                <EditableValue
                                    value={parsedBorderRadius.value}
                                    defaultValue={borderRadiusParams.defaultValue}
                                    onChange={(val) => {
                                        if (val === undefined) {
                                            updateLocalMetricsConfig({ borderRadius: undefined });
                                        } else {
                                            updateLocalMetricsConfig({
                                                borderRadius: `${val}${parsedBorderRadius.unit}`,
                                            });
                                        }
                                    }}
                                    min={borderRadiusParams.min}
                                    max={borderRadiusParams.max}
                                    unit={parsedBorderRadius.unit}
                                    units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                    onUnitChange={(newUnit) => {
                                        const currentVal =
                                            parsedBorderRadius.value != null
                                                ? parsedBorderRadius.value
                                                : getSliderParams(newUnit, 'borderRadius').defaultValue;
                                        updateLocalMetricsConfig({
                                            borderRadius: `${currentVal}${newUnit}`,
                                        });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="range"
                                    id="detachedMetricsBorderRadius"
                                    className="flex-grow-1"
                                    min={borderRadiusParams.min}
                                    max={borderRadiusParams.max}
                                    step={borderRadiusParams.step}
                                    value={
                                        parsedBorderRadius.value != null
                                            ? parsedBorderRadius.value
                                            : borderRadiusParams.defaultValue
                                    }
                                    onChange={(e) =>
                                        updateLocalMetricsConfig({
                                            borderRadius: `${parseFloat(e.target.value)}${parsedBorderRadius.unit}`,
                                        })
                                    }
                                    onPointerDown={() => {
                                        if (parsedBorderRadius.value == null) {
                                            updateLocalMetricsConfig({
                                                borderRadius: `${borderRadiusParams.defaultValue}${parsedBorderRadius.unit}`,
                                            });
                                        }
                                    }}
                                    style={{
                                        opacity: parsedBorderRadius.value != null ? 1 : 0.5,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                        detachedMetricsBorderRadiusRaw != null
                                            ? 'btn-outline-secondary'
                                            : 'btn-outline-secondary disabled'
                                    }`}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        padding: 0,
                                        opacity: detachedMetricsBorderRadiusRaw != null ? 1 : 0.4,
                                        pointerEvents: detachedMetricsBorderRadiusRaw != null ? 'auto' : 'none',
                                    }}
                                    onClick={() =>
                                        updateLocalMetricsConfig({
                                            borderRadius: undefined,
                                        })
                                    }
                                    title={
                                        detachedMetricsBorderRadiusRaw != null
                                            ? 'Wert zurücksetzen'
                                            : 'Standardwert aktiv'
                                    }
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </FormGroup>
                    </div>
                )}

                <div className="ps-3 border-start mb-3">
                    <FormGroup className="mb-3">
                        <div className="d-flex justify-content-between mb-2">
                            <Label className="fw-semibold" for="metricsGapValueDesktop">
                                Abstand unten (Desktop)
                            </Label>
                            <EditableValue
                                value={parsedGapDesktop.value}
                                defaultValue={gapDesktopParams.defaultValue}
                                onChange={(val) => {
                                    if (val === undefined) {
                                        updateLocalMetricsConfig({
                                            gap: {
                                                ...gap,
                                                desktop: undefined,
                                            },
                                        });
                                    } else {
                                        updateLocalMetricsConfig({
                                            gap: {
                                                ...gap,
                                                desktop: `${val}${parsedGapDesktop.unit}`,
                                            },
                                        });
                                    }
                                }}
                                min={gapDesktopParams.min}
                                max={gapDesktopParams.max}
                                unit={parsedGapDesktop.unit}
                                units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                onUnitChange={(newUnit) => {
                                    const currentVal =
                                        parsedGapDesktop.value != null
                                            ? parsedGapDesktop.value
                                            : getSliderParams(newUnit, 'gap').defaultValue;
                                    updateLocalMetricsConfig({
                                        gap: {
                                            ...gap,
                                            desktop: `${currentVal}${newUnit}`,
                                        },
                                    });
                                }}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Input
                                type="range"
                                id="metricsGapValueDesktop"
                                className="flex-grow-1"
                                min={gapDesktopParams.min}
                                max={gapDesktopParams.max}
                                step={gapDesktopParams.step}
                                value={
                                    parsedGapDesktop.value != null
                                        ? parsedGapDesktop.value
                                        : gapDesktopParams.defaultValue
                                }
                                onChange={(e) =>
                                    updateLocalMetricsConfig({
                                        gap: {
                                            ...gap,
                                            desktop: `${parseFloat(e.target.value)}${parsedGapDesktop.unit}`,
                                        },
                                    })
                                }
                                onPointerDown={() => {
                                    if (parsedGapDesktop.value == null) {
                                        updateLocalMetricsConfig({
                                            gap: {
                                                ...gap,
                                                desktop: `${gapDesktopParams.defaultValue}${parsedGapDesktop.unit}`,
                                            },
                                        });
                                    }
                                }}
                                style={{
                                    opacity: parsedGapDesktop.value != null ? 1 : 0.5,
                                    transition: 'opacity 0.15s ease',
                                }}
                            />
                            <button
                                type="button"
                                className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                    metricsGapValueDesktopRaw != null
                                        ? 'btn-outline-secondary'
                                        : 'btn-outline-secondary disabled'
                                }`}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    padding: 0,
                                    opacity: metricsGapValueDesktopRaw != null ? 1 : 0.4,
                                    pointerEvents: metricsGapValueDesktopRaw != null ? 'auto' : 'none',
                                }}
                                onClick={() =>
                                    updateLocalMetricsConfig({
                                        gap: {
                                            ...gap,
                                            desktop: undefined,
                                        },
                                    })
                                }
                                title={metricsGapValueDesktopRaw != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                            >
                                <IconX size={14} />
                            </button>
                        </div>
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <div className="d-flex justify-content-between mb-2">
                            <Label className="fw-semibold" for="metricsGapValueMobile">
                                Abstand unten (Mobil)
                            </Label>
                            <EditableValue
                                value={parsedGapMobile.value}
                                defaultValue={gapMobileParams.defaultValue}
                                onChange={(val) => {
                                    if (val === undefined) {
                                        updateLocalMetricsConfig({
                                            gap: {
                                                ...gap,
                                                mobile: undefined,
                                            },
                                        });
                                    } else {
                                        updateLocalMetricsConfig({
                                            gap: {
                                                ...gap,
                                                mobile: `${val}${parsedGapMobile.unit}`,
                                            },
                                        });
                                    }
                                }}
                                min={gapMobileParams.min}
                                max={gapMobileParams.max}
                                unit={parsedGapMobile.unit}
                                units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                onUnitChange={(newUnit) => {
                                    const currentVal =
                                        parsedGapMobile.value != null
                                            ? parsedGapMobile.value
                                            : getSliderParams(newUnit, 'gap').defaultValue;
                                    updateLocalMetricsConfig({
                                        gap: {
                                            ...gap,
                                            mobile: `${currentVal}${newUnit}`,
                                        },
                                    });
                                }}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Input
                                type="range"
                                id="metricsGapValueMobile"
                                className="flex-grow-1"
                                min={gapMobileParams.min}
                                max={gapMobileParams.max}
                                step={gapMobileParams.step}
                                value={
                                    parsedGapMobile.value != null ? parsedGapMobile.value : gapMobileParams.defaultValue
                                }
                                onChange={(e) =>
                                    updateLocalMetricsConfig({
                                        gap: {
                                            ...gap,
                                            mobile: `${parseFloat(e.target.value)}${parsedGapMobile.unit}`,
                                        },
                                    })
                                }
                                onPointerDown={() => {
                                    if (parsedGapMobile.value == null) {
                                        updateLocalMetricsConfig({
                                            gap: {
                                                ...gap,
                                                mobile: `${gapMobileParams.defaultValue}${parsedGapMobile.unit}`,
                                            },
                                        });
                                    }
                                }}
                                style={{
                                    opacity: parsedGapMobile.value != null ? 1 : 0.5,
                                    transition: 'opacity 0.15s ease',
                                }}
                            />
                            <button
                                type="button"
                                className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                    metricsGapValueMobileRaw != null
                                        ? 'btn-outline-secondary'
                                        : 'btn-outline-secondary disabled'
                                }`}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    padding: 0,
                                    opacity: metricsGapValueMobileRaw != null ? 1 : 0.4,
                                    pointerEvents: metricsGapValueMobileRaw != null ? 'auto' : 'none',
                                }}
                                onClick={() =>
                                    updateLocalMetricsConfig({
                                        gap: {
                                            ...gap,
                                            mobile: undefined,
                                        },
                                    })
                                }
                                title={metricsGapValueMobileRaw != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                            >
                                <IconX size={14} />
                            </button>
                        </div>
                    </FormGroup>
                </div>
            </div>
        </div>
    );
};

export default CampaignMetricsTab;
